import { evaluateCondition } from '@dynamic/ui';
import { useEffect } from 'react';
import { useLocation } from 'react-router';

import { Condition } from '#/client/models';
import { getNavigationTarget } from '#/components/Header/Menu.utils';
import { PageAliasesConfig } from '#/service/config/common.types';
import { MoreItem } from '#/service/config/more.types';
import { Path } from '#/service/routes';
import { useAuthStore } from '#/store/auth/auth.store';

import { useConfig } from './useConfig';
import { useDataContext } from './useDataContext';
import { useNavigation } from './useNavigation';

/**
 * A guard that validates if the user can access the current page by validating
 * conditional routes. It only takes settings conditions into account, and ignores
 * menu item conditions. If the condition evaluation returns false then
 * redirects to the home page. Registration route is excluded from the guard.
 */
export function usePageGuard() {
  const { pathname } = useLocation();
  const { isLoggedIn, isInitialised } = useAuthStore();

  const { getGeneralContext } = useDataContext();
  const context = getGeneralContext();
  const conditionalRoutes = useConditionalRoutes();
  const { goHome } = useNavigation(`usePageGuard on ${pathname}`);

  useEffect(() => {
    if (!isInitialised) {
      return;
    }

    // registration handles its own redirections
    if (pathname === Path.Registration || !conditionalRoutes) {
      return;
    }

    const condition = conditionalRoutes[pathname];

    // the page we are on is no longer available due to its condition
    const isPageGone = condition && !evaluateCondition(condition, context);

    if (isPageGone) {
      goHome();
    }
  }, [conditionalRoutes, isLoggedIn, context, pathname, goHome, isInitialised]);
}

export function useConditionalRoutes() {
  const {
    more: { settings },
    pages,
  } = useConfig();

  return getRoutesFromSettings(settings, pages) ?? {};
}

function getRoutesFromSettings(
  settings: MoreItem[],
  pages: PageAliasesConfig
): RoutesConditions {
  return settings?.reduce((acc, item) => {
    const pathname = getSettingItemTargetUrl(item, pages);

    return { ...acc, [pathname]: item.condition };
  }, {});
}

type RoutesConditions = { [pathname: string]: Condition };

function getSettingItemTargetUrl(
  item: MoreItem,
  pages: PageAliasesConfig
): string {
  const settingUrl = '/settings/' + item.key;

  // we don't care about external urls on purpose
  const { internalUrl } = getNavigationTarget(item, '', pages);

  return internalUrl ?? settingUrl;
}
